import { Button, FormItem, Icon, TextField } from '@getsynapse/design-system';
import classnames from 'classnames';
import React from 'react';
import intl from 'react-intl-universal';
import NullableDropdown from '../../../Organisms/NullableDropdown/NullableDropdown';
import { EDIT_BUDGET_TABS } from '../../../utils/constants';
import {
  BudgetModel,
  BudgetSourceEnum,
  CreateLineItemRequest,
  LineItemWithAmounts,
  Option,
  ProjectToProgramMap,
  TimeFrame,
  UpdateBudgetAmountRequest,
} from '../../../utils/customTypes';
import { ParentOfLineItems } from '../../../utils/types/budget';
import {
  calculateTimeframeCellValue,
  calculateTotalAllocatedTimeframeCellValue,
  calculateTotalAnnualValue,
  mapRowColorWithLayer,
  numberFormat,
} from './budgetHelper';
import programIcon from 'assets/icons/program.svg';
import projectIcon from 'assets/icons/projects.svg';
import CurrencyField from 'Atoms/CurrencyField/CurrencyField';

let totalAllocatedRawAmounts: number[] = [];
let totalBudgetRawAmounts: number[] = [];
let totalVarianceRawAmounts: number[] = [];

const cellClass157px = 'min-w-157px h-12 py-0 px-1';
const cellClass157pxInsideDiv = 'min-w-157px h-full py-1 flex items-center';

const cellClass240px = 'min-w-240px h-12 py-0 px-1';
const cellClass240pxInsideDiv = 'min-w-240px h-full py-1 flex items-center';

export function generateMultipleRows(
  parentList: Array<ParentOfLineItems>,
  layer: number,
  expandedRows: Record<string, boolean>,
  toggleExpandedRow: (rowId: string) => void,
  focusedRows: Record<string, boolean>,
  toggleFocusedRow: (params: {
    rowId: string;
    isFocused?: boolean;
    isOnlyChecked?: boolean;
  }) => void,
  checkedRows: Record<string, boolean>,
  handleAddNote: (item: LineItemWithAmounts) => void,
  handleAddAttachment: (itemId: string) => void,
  handleDeleteLineItem: (item: LineItemWithAmounts) => void,
  onLineItemSaved: (lineItemRequest: CreateLineItemRequest) => void,
  onAddNewLineItem: (
    parentType: string,
    parentId?: string,
    parentOfParentId?: string
  ) => void,
  onEditLineItem: (
    lineItemReq: LineItemWithAmounts,
    field?: string,
    value?: string,
    amounts?: number[]
  ) => void,
  onEditCostOfLabor: (
    projectId: string,
    lineItemId?: string,
    amounts?: number[]
  ) => void,
  programOptions: Option[],
  projectToProgramMap: ProjectToProgramMap,
  vendorOptions: Option[],
  teamOptions: Option[],
  projectOptions: Option[],
  viewType: string,
  fiscalMonthStart: number,
  timeframe: TimeFrame,
  lineItems: LineItemWithAmounts[],
  budgetAmounts: number[],
  onEditBudgetAmount: (budgetAmountReq: UpdateBudgetAmountRequest) => void,
  costOfLaborFilter: boolean,
  laborOfCostLineItems: LineItemWithAmounts[],
  focusedCellId: string,
  setFocusedCellId: (id: string) => void,
  selectedBudget?: BudgetModel,
  parentId?: string
): Array<any> {
  let rows = parentList.map((parentInfo) => {
    const isExpanded = expandedRows[parentInfo.id];
    const annualTotalColumn = [];
    if (timeframe !== TimeFrame.YEARLY) {
      annualTotalColumn.push({
        content: '$' + numberFormat(calculateTotalAnnualValue(parentInfo)),
        className: 'text-right',
      });
    }
    return [
      {
        id: `budget-${parentInfo.id}-details`,
        className: `group border ${mapRowColorWithLayer(layer)}`,
        cells: [
          {
            content: (
              <div
                className='flex flex-row gap-x-1 cursor-pointer min-w-200px'
                style={{ paddingLeft: `${layer * 10}px` }}
                onClick={() => toggleExpandedRow(parentInfo.id)}
              >
                {isExpanded ? (
                  <Icon name='chevron-down-outline' className='text-xl' />
                ) : (
                  <Icon name='chevron-forward-outline' className='text-xl' />
                )}
                {parentInfo.type === 'programId' &&
                  parentInfo.id !== 'none' && (
                    <Icon src={programIcon} className='text-xl' />
                  )}
                {parentInfo.type === 'projectId' &&
                  parentInfo.id !== 'none' && (
                    <Icon src={projectIcon} className='text-xl' />
                  )}
                <div className='table-text-truncate flex items-center overflow-hidden'>
                  {parentInfo.title}
                </div>
              </div>
            ),
            className: stickyCellsStyles(
              classnames('min-w-200px left-0 py-0 px-1 h-12'),
              layer
            ),
          },
          ...annualTotalColumn,
          ...Array.from({
            length:
              timeframe === TimeFrame.QUARTERLY
                ? 4
                : timeframe === TimeFrame.MONTHLY
                ? 12
                : 1,
          }).map((_, index) => ({
            content:
              '$' +
              numberFormat(
                calculateTimeframeCellValue(
                  parentInfo,
                  timeframe,
                  fiscalMonthStart,
                  index
                )
              ),
            className: 'text-right',
          })),
          ...generate157pxEmptyCells(
            viewType === EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT ? 3 : 4
          ),
          generateActionCell(),
        ].filter(Boolean),
      },
      ...(isExpanded
        ? generateLineItemRows(
            layer + 1,
            parentInfo.lineItems || [],
            handleAddNote,
            handleAddAttachment,
            handleDeleteLineItem,
            onEditLineItem,
            programOptions,
            projectToProgramMap,
            vendorOptions,
            teamOptions,
            projectOptions,
            viewType,
            fiscalMonthStart,
            timeframe
          ).flat()
        : []),
      ...(isExpanded
        ? generateAddLineItem(
            layer + 1,
            timeframe,
            onAddNewLineItem,
            viewType,
            parentInfo.type,
            parentInfo.id === 'none' ? undefined : parentInfo.id,
            parentId
          ).flat()
        : []),
      ...(isExpanded && costOfLaborFilter && parentInfo.type === 'projectId'
        ? generateCostOfLaborRow(
            layer + 1,
            onEditCostOfLabor,
            viewType,
            fiscalMonthStart,
            timeframe,
            parentInfo.id,
            handleAddNote,
            handleAddAttachment,
            laborOfCostLineItems.find(
              (item) => item.projectId === parentInfo.id
            )
          ).flat()
        : []),
      // Expanded Row for children (if expanded)
      ...(isExpanded && parentInfo.children?.length
        ? generateMultipleRows(
            parentInfo.children || [],
            layer + 1,
            expandedRows,
            toggleExpandedRow,
            focusedRows,
            toggleFocusedRow,
            checkedRows,
            handleAddNote,
            handleAddAttachment,
            handleDeleteLineItem,
            onLineItemSaved,
            onAddNewLineItem,
            onEditLineItem,
            onEditCostOfLabor,
            programOptions,
            projectToProgramMap,
            vendorOptions,
            teamOptions,
            projectOptions,
            viewType,
            fiscalMonthStart,
            timeframe,
            lineItems,
            budgetAmounts,
            onEditBudgetAmount,
            costOfLaborFilter,
            laborOfCostLineItems,
            focusedCellId,
            setFocusedCellId,
            selectedBudget,
            parentInfo.id
          ).flat()
        : []),
    ];
  });

  if (layer === 0) {
    const totalAllocatedRow = generateTotalAllocated(
      viewType === EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT && costOfLaborFilter
        ? [...lineItems, ...laborOfCostLineItems]
        : lineItems,
      fiscalMonthStart,
      timeframe,
      viewType
    );

    const budgetAmountRow = selectedBudget
      ? generateBudgetAmountRow(
          timeframe,
          budgetAmounts,
          onEditBudgetAmount,
          selectedBudget,
          viewType
        )
      : [];

    totalVarianceRawAmounts = [];

    for (let i = 0; i < budgetAmounts.length; i++) {
      totalVarianceRawAmounts.push(
        totalBudgetRawAmounts[i] - totalAllocatedRawAmounts[i]
      );
    }

    const varianceRow = selectedBudget
      ? generateVarianceRow(totalVarianceRawAmounts || [], timeframe, viewType)
      : [];

    rows.push(totalAllocatedRow);
    rows.push(budgetAmountRow);
    rows.push(varianceRow);
  }
  return rows;
}

export function generateLineItemRows(
  layer: number,
  lineItemList: Array<LineItemWithAmounts>,
  handleAddNote: (item: LineItemWithAmounts) => void,
  handleAddAttachment: (itemId: string) => void,
  handleDeleteLineItem: (item: LineItemWithAmounts) => void,
  onEditLineItem: (
    lineItemReq: LineItemWithAmounts,
    field?: string,
    value?: string,
    amounts?: number[]
  ) => void,
  programOptions: Option[],
  projectToProgramMap: ProjectToProgramMap,
  vendorOptions: Option[],
  teamOptions: Option[],
  projectOptions: Option[],
  viewType: string,
  fiscalMonthStart: number,
  timeframe: TimeFrame
): Array<any> {
  const getProgramOptions = (projectId?: string | null) => {
    if (projectId) {
      const programIds = projectToProgramMap[projectId];
      if (programIds) {
        return programOptions.filter((i) => programIds.has(i.value));
      } else {
        return [];
      }
    }
    return programOptions;
  };

  const getProjectOptions = (programId?: string | null) => {
    if (programId) {
      return projectOptions.filter((option) => {
        const programIds = projectToProgramMap[option.value];
        return programIds && programIds.has(programId);
      });
    }
    return projectOptions;
  };

  function isProjectAndProgramLinked(
    projectId: string | undefined,
    programId: string | undefined
  ) {
    return (
      !projectToProgramMap[projectId || '']?.has(programId || '') &&
      programId &&
      projectId
    );
  }

  const getAnnualTotalCells = (rawAmounts: number[], timeFrame: TimeFrame) => {
    if (timeFrame === TimeFrame.YEARLY) {
      return [];
    }
    return [
      {
        content: (
          <div className='overflow-hidden text-right px-3'>
            {'$' +
              numberFormat(
                rawAmounts.reduce(
                  (total: number, item: number) => total + (item ?? 0),
                  0
                )
              )}
          </div>
        ),
        className: cellClass157px,
      },
    ];
  };

  return lineItemList
    .filter((lineItemInfo) => lineItemInfo.description !== 'Cost of Labor')
    .map((lineItemInfo) => {
      return [
        {
          id: `budget-line-item-${lineItemInfo.id}-details`,
          className: `group border ${mapRowColorWithLayer(2)} h-12`,
          cells: [
            {
              id: `line-item-${lineItemInfo.id}-description`,
              content: (
                // Keep the save handlers on this wrapper, not on the
                // TextField: when the text overflows the visible width, the
                // design-system TextField wraps its input in a Tooltip whose
                // cloneElement overwrites the input's own onBlur/onKeyDown
                // (CGP-213). Bubbled keydown/focusout events still reach this
                // wrapper either way.
                <div
                  className='h-full min-w-200px pl-2 py-1'
                  style={{ paddingLeft: `${layer * 10}px` }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                    if (e.key === 'Enter') {
                      (e.target as HTMLInputElement).blur(); // Triggers onBlur
                    }
                  }}
                  onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
                    // e.target is whichever descendant blurred, so `value` is
                    // not guaranteed to exist - guard it.
                    const value = (e.target as HTMLInputElement).value;
                    if (!value?.trim()) {
                      return;
                    }
                    onEditLineItem(lineItemInfo, 'description', value);
                  }}
                >
                  <TextField
                    autoFocus={lineItemInfo.showInput}
                    className='h-full w-full'
                    inputClassName='h-full'
                    value={lineItemInfo.description}
                    maxLength={100}
                    maxLengthHelpText='Should be less than 100 characters'
                  />
                </div>
              ),
              className: stickyCellsStyles(
                classnames('min-w-200px left-0 h-12 p-0'),
                2
              ),
            },
            ...getAnnualTotalCells(lineItemInfo.rawAmounts, timeframe),
            ...Array.from({
              length:
                timeframe === TimeFrame.QUARTERLY
                  ? 4
                  : timeframe === TimeFrame.MONTHLY
                  ? 12
                  : 1,
            }).map((_, index) => ({
              content: (
                <div className={cellClass157pxInsideDiv + ' h-12 text-right'}>
                  <CurrencyField
                    readOnly={!lineItemInfo.description}
                    className='h-full w-full'
                    inputClassName='h-full text-right'
                    value={
                      timeframe === TimeFrame.MONTHLY
                        ? (
                            lineItemInfo.rawAmounts[
                              (fiscalMonthStart + index) % 12
                            ] ?? 0
                          ).toFixed(2)
                        : (lineItemInfo.rawAmounts[index] ?? 0).toFixed(2)
                    }
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur(); // Triggers onBlur
                      }
                    }}
                    onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      const { value, selectionStart } = e.currentTarget;
                      const key = e.key;

                      // Allow numbers, one decimal, and navigation keys (Backspace, Arrow keys)
                      if (
                        !/[0-9.]/.test(key) &&
                        !['Backspace', 'ArrowLeft', 'ArrowRight'].includes(key)
                      ) {
                        e.preventDefault();
                      }

                      // Prevent multiple decimal points
                      if (key === '.' && value.includes('.')) {
                        e.preventDefault();
                      }

                      // Ensure only up to two decimal places
                      if (
                        value.includes('.') &&
                        selectionStart !== null &&
                        value.indexOf('.') < selectionStart && // Only enforce if editing decimal
                        // part
                        value.split('.')[1]?.length >= 2
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onBlur={(e: any) => {
                      let hold = '';
                      // Ensure amounts array is exactly 12 elements for monthly timeframe
                      let amounts = Array.from(
                        { length: 12 },
                        (_, i) => lineItemInfo.rawAmounts[i] ?? 0
                      );
                      if (timeframe === TimeFrame.MONTHLY) {
                        const monthlyIndex = (fiscalMonthStart + index) % 12;
                        hold = amounts[monthlyIndex].toString();
                        amounts[monthlyIndex] = Number(e.target.value);
                      } else {
                        hold = amounts[index].toString();
                        amounts[index] = e.target.value;
                      }

                      if (Number(hold) !== Number(e.target.value)) {
                        onEditLineItem(
                          lineItemInfo,
                          undefined,
                          undefined,
                          amounts
                        );
                      }
                    }}
                  />
                </div>
              ),
              className: cellClass157px,
            })),
            // team
            viewType !== EDIT_BUDGET_TABS.TEAMS
              ? {
                  content: (
                    <div className={cellClass240pxInsideDiv}>
                      <NullableDropdown
                        popperDiffWidth={true}
                        triggerProps={{
                          className: 'h-10',
                        }}
                        disabled={!lineItemInfo.description}
                        options={teamOptions}
                        onChange={(option: Option) => {
                          onEditLineItem(
                            lineItemInfo,
                            'learningTeamId',
                            option.value
                          );
                        }}
                        values={
                          lineItemInfo.learningTeamId
                            ? [
                                {
                                  label: teamOptions.find(
                                    (team) =>
                                      team.value === lineItemInfo.learningTeamId
                                  )?.label,
                                  value: lineItemInfo.learningTeamId,
                                },
                              ]
                            : []
                        }
                        placeholder='Select Team'
                      />
                    </div>
                  ),
                  className: cellClass240px,
                }
              : null,
            // program
            viewType !== EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT
              ? {
                  content: (
                    <div className={cellClass240pxInsideDiv}>
                      <NullableDropdown
                        popperDiffWidth={true}
                        triggerProps={{
                          className: 'h-10',
                        }}
                        className={`${
                          isProjectAndProgramLinked(
                            lineItemInfo.projectId,
                            lineItemInfo.programId
                          )
                            ? 'border border-error-dark rounded'
                            : ''
                        }`}
                        options={getProgramOptions(lineItemInfo.projectId)}
                        onChange={(option: Option) => {
                          onEditLineItem(
                            lineItemInfo,
                            'programId',
                            option.value
                          );
                        }}
                        values={
                          lineItemInfo.programId
                            ? [
                                {
                                  label: programOptions.find(
                                    (program) =>
                                      program.value === lineItemInfo.programId
                                  )?.label,
                                  value: lineItemInfo.programId,
                                },
                              ]
                            : []
                        }
                        placeholder='Select Program'
                        filterable
                      />
                    </div>
                  ),
                  className: cellClass240px,
                }
              : null,

            // project
            viewType !== EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT
              ? {
                  content: (
                    <div className={cellClass240pxInsideDiv}>
                      <NullableDropdown
                        popperDiffWidth={true}
                        triggerProps={{
                          className: 'h-10',
                        }}
                        disabled={!lineItemInfo.description}
                        options={getProjectOptions(lineItemInfo.programId)}
                        onChange={(option: Option) => {
                          onEditLineItem(
                            lineItemInfo,
                            'projectId',
                            option.value
                          );
                        }}
                        values={
                          lineItemInfo.projectId
                            ? [
                                {
                                  label: projectOptions.find(
                                    (option) =>
                                      option.value === lineItemInfo.projectId
                                  )?.label,
                                  value: lineItemInfo.projectId,
                                },
                              ]
                            : []
                        }
                        placeholder='Select Project'
                        filterable
                      />
                    </div>
                  ),
                  className: cellClass240px,
                }
              : null,

            // Budget Source
            {
              content: (
                <div className={cellClass157pxInsideDiv}>
                  <NullableDropdown
                    popperDiffWidth={true}
                    triggerProps={{
                      className: 'h-10',
                    }}
                    disabled={!lineItemInfo.description}
                    options={Object.values(BudgetSourceEnum).map((value) => ({
                      value,
                      label: value,
                    }))}
                    onChange={(option: Option) => {
                      onEditLineItem(
                        lineItemInfo,
                        'budgetSource',
                        option.value
                      );
                    }}
                    values={
                      lineItemInfo.budgetSource
                        ? [
                            {
                              label: lineItemInfo.budgetSource,
                              value: lineItemInfo.budgetSource,
                            },
                          ]
                        : []
                    }
                    placeholder='Budget Source'
                  />
                </div>
              ),
              className: cellClass157px,
            },

            // vendor
            viewType !== EDIT_BUDGET_TABS.VENDORS
              ? {
                  content: (
                    <div className={cellClass240pxInsideDiv}>
                      <NullableDropdown
                        popperDiffWidth={true}
                        triggerProps={{
                          className: 'h-10',
                        }}
                        disabled={!lineItemInfo.description}
                        options={vendorOptions}
                        onChange={(option: Option) => {
                          onEditLineItem(
                            lineItemInfo,
                            'vendorId',
                            option.value
                          );
                        }}
                        values={
                          lineItemInfo.vendorId
                            ? [
                                {
                                  label: vendorOptions.find(
                                    (vendor) =>
                                      vendor.value === lineItemInfo.vendorId
                                  )?.label,
                                  value: lineItemInfo.vendorId,
                                },
                              ]
                            : []
                        }
                        placeholder='Select Vendor'
                      />
                    </div>
                  ),
                  className: cellClass240px,
                }
              : null,

            {
              content: (
                <div className='min-w-120px px-3 grid grid-cols-3 gap-x-2'>
                  <Button
                    variant='tertiary'
                    onClick={() => {
                      handleAddNote(lineItemInfo);
                    }}
                    className='w-6 h-6 px-1'
                  >
                    <div className='action-icon-wrapper '>
                      <Icon name='chatbox-outline' />
                      {lineItemInfo.note && <div className='active-dot'></div>}
                    </div>
                  </Button>
                  <Button
                    variant='tertiary'
                    onClick={() => {
                      handleAddAttachment(lineItemInfo.id);
                    }}
                    className='w-6 h-6 px-1'
                  >
                    <div className='action-icon-wrapper'>
                      <Icon name='attach' />
                      {lineItemInfo.hasFile && (
                        <div className='active-dot'></div>
                      )}
                    </div>
                  </Button>
                  <Button
                    variant='tertiary'
                    onClick={() => {
                      handleDeleteLineItem(lineItemInfo);
                    }}
                    className='w-6 h-6 px-1'
                  >
                    <Icon name='trash' />
                  </Button>
                </div>
              ),
              className: 'h-12 min-w-120px py-0 px-1',
            },
          ].filter(Boolean),
        },
      ];
    });
}

export function generateBrokenLineItemRows(
  brokenLineItemList: Array<LineItemWithAmounts>,
  onEditLineItem: (lineItem: string, field?: string, value?: string) => void,
  programOptions: Option[],
  projectToProgramMap: ProjectToProgramMap,
  teamOptions: Option[],
  projectOptions: Option[],
  selectedProgramIds: Record<string, string | null>,
  onProgramChange: (lineItemId: string, programId: string) => void,
  applyToAll: (
    updatedProgramId: string,
    matchingLineItems: Array<LineItemWithAmounts>
  ) => void,
  setHaveChanged: (changed: boolean) => void
): Array<any> {
  const getProgramOptions = (projectId?: string | null) => {
    if (projectId) {
      const programIds = projectToProgramMap[projectId];
      if (programIds) {
        return programOptions.filter((i) => programIds.has(i.value));
      } else {
        return [];
      }
    }
    return programOptions;
  };

  function isProjectAndProgramLinked(
    projectId: string | undefined,
    programId: string | undefined
  ) {
    return (
      !projectToProgramMap[projectId || '']?.has(programId || '') && programId
    );
  }

  return brokenLineItemList.map((lineItemInfo) => {
    const selectedProgramId = selectedProgramIds[lineItemInfo.id] || null;
    return [
      {
        id: `line-item-${lineItemInfo.id}-details`,
        className: `group border bg-neutral-lightest-two h-20`,
        cells: [
          {
            content: (
              <div
                className='flex flex-row min-w-200px cursor-pointer table-text-truncate items-center'
                style={{ paddingLeft: `10px` }}
              >
                {lineItemInfo.description}
              </div>
            ),
            className: stickyCellsStyles(
              classnames('min-w-200px left-0 py-0 px-1'),
              2
            ),
          },
          {
            content: (
              <div
                className={`h-10 cursor-pointer overflow-hidden table-text-truncate flex items-center ${
                  !lineItemInfo.learningTeamId
                    ? 'hover:border hover:border-neutral-light hover:rounded-xl'
                    : ''
                }`}
              >
                {lineItemInfo.learningTeamId
                  ? teamOptions.find(
                      (team) => team.value === lineItemInfo.learningTeamId
                    )?.label
                  : ''}
              </div>
            ),
          },
          // program
          {
            content: (
              <div className='w-52 h-6'>
                <FormItem
                  labelProps={{
                    state: isProjectAndProgramLinked(
                      lineItemInfo.projectId,
                      lineItemInfo.programId
                    )
                      ? 'error'
                      : 'default',
                  }}
                  helpText={
                    isProjectAndProgramLinked(
                      lineItemInfo.projectId,
                      lineItemInfo.programId
                    ) &&
                    intl.get(
                      'BUDGET_PAGE.ERRORS.NOT_RELATED_TO_SELECTED_PROJECT'
                    )
                  }
                  helpTextProps={{
                    state: isProjectAndProgramLinked(
                      lineItemInfo.projectId,
                      lineItemInfo.programId
                    )
                      ? 'error'
                      : 'default',
                  }}
                  className='col-start-1 col-end-1 col-span-2'
                >
                  <NullableDropdown
                    popperDiffWidth={true}
                    className={`w-52 
                      ${
                        isProjectAndProgramLinked(
                          lineItemInfo.projectId,
                          lineItemInfo.programId
                        )
                          ? 'border border-error-dark rounded'
                          : ''
                      }`}
                    triggerProps={{ size: 'sm' }}
                    options={getProgramOptions(lineItemInfo.projectId)}
                    onChange={(option: Option) => {
                      onEditLineItem(
                        lineItemInfo.id,
                        'programId',
                        option.value
                      );
                      onProgramChange(lineItemInfo.id, option.value);
                      setHaveChanged(true);
                    }}
                    values={
                      lineItemInfo.programId
                        ? [
                            {
                              label: programOptions.find(
                                (program) =>
                                  program.value === lineItemInfo.programId
                              )?.label,
                              value: lineItemInfo.programId,
                            },
                          ]
                        : []
                    }
                    placeholder='Select Program'
                  />
                </FormItem>
              </div>
            ),
          },
          //projects
          {
            content: (
              <div className='w-60 h-6'>
                <NullableDropdown
                  popperDiffWidth={true}
                  className='w-60'
                  triggerProps={{ size: 'sm' }}
                  options={projectOptions}
                  onChange={(option: Option) => {
                    onEditLineItem(lineItemInfo.id, 'projectId', option.value);
                  }}
                  readOnly={true}
                  values={
                    lineItemInfo.projectId
                      ? [
                          {
                            label: projectOptions.find(
                              (option) =>
                                option.value === lineItemInfo.projectId
                            )?.label,
                            value: lineItemInfo.projectId,
                          },
                        ]
                      : []
                  }
                  placeholder='Select Project'
                />
              </div>
            ),
          },
          {
            content: (
              <div className='mt-2 flex flex-row w-28'>
                <Button
                  variant='secondary'
                  disabled={
                    !projectToProgramMap[lineItemInfo.projectId || '']?.has(
                      lineItemInfo.programId || ''
                    )
                  }
                  data-cy='apply-to-all-button'
                  onClick={() => {
                    const matchingLineItems = brokenLineItemList.filter(
                      (item) => item.projectId === lineItemInfo.projectId
                    );
                    applyToAll(selectedProgramId || '', matchingLineItems);
                  }}
                >
                  {intl.get('BUDGET_PAGE.BROKEN_LINE_ITEMS_MODAL.APPLY_TO_ALL')}
                </Button>
              </div>
            ),
          },
        ].filter(Boolean),
      },
    ];
  });
}

export function generateAddLineItem(
  layer: number,
  timeframe: TimeFrame,
  onAddNewLineItem: (
    parentType: string,
    parentId?: string,
    parentOfParentId?: string
  ) => void,
  viewType: string,
  parentType: string,
  parentId?: string,
  parentOfParentId?: string
): Array<any> {
  let addLineItem = [];
  let addLineItemRow = {
    id: 'newInput',
    className: `group border`,
    cells: [
      {
        content: (
          <button
            className='h-12 flex flex-row gap-x-2 items-center'
            style={{ paddingLeft: `${layer * 10}px` }}
            onClick={() => {
              onAddNewLineItem(parentType, parentId, parentOfParentId);
            }}
          >
            <div className='text-sm'>Add Line Item</div>
            <Icon name='add-outline' className='text-xl' />
          </button>
        ),
        className: stickyCellsStyles(
          classnames('min-w-200px left-0 py-0 px-2 h-12'),
          4
        ),
      },
      ...generate157pxEmptyCells(timeframe === TimeFrame.YEARLY ? 0 : 1),
      ...generateTableEmptyColumnForTimeframes(timeframe),
      ...generate157pxEmptyCells(
        viewType === EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT ? 3 : 4
      ),
      generateActionCell(),
    ].filter(Boolean),
  };
  addLineItem.push(addLineItemRow);
  return addLineItem;
}

export function generateTotalAllocated(
  lineItemList: Array<LineItemWithAmounts>,
  fiscalMonthStart: number,
  timeframe: TimeFrame,
  viewType: string
) {
  const totalRawAmount = lineItemList.reduce((total, item) => {
    const itemTotal = item.rawAmounts.reduce((sum, amount) => sum + amount, 0);
    return total + itemTotal;
  }, 0);

  const annualTotalCell = [];
  if (timeframe !== TimeFrame.YEARLY) {
    annualTotalCell.push({
      content: (
        <div className='text-neutral-dark text-right px-3'>
          {'$' + numberFormat(totalRawAmount)}
        </div>
      ),
      className: cellClass157px,
    });
  }

  totalAllocatedRawAmounts = [];

  return [
    {
      id: `123`,
      className: `group border ${mapRowColorWithLayer(2)}`,

      cells: [
        {
          content: (
            <div
              className='flex flex-row gap-x-1 min-w-200px cursor-pointer'
              style={{ paddingLeft: `${10}px` }}
            >
              <div className='text-neutral-dark'>Total Allocated</div>
            </div>
          ),
          className: stickyCellsStyles(
            classnames('min-w-200px left-0 py-0 px-1'),
            2
          ),
        },
        ...annualTotalCell,
        ...Array.from({
          length:
            timeframe === TimeFrame.QUARTERLY
              ? 4
              : timeframe === TimeFrame.MONTHLY
              ? 12
              : 1,
        }).map((_, index) => {
          const formattedValue = calculateTotalAllocatedTimeframeCellValue(
            lineItemList,
            timeframe,
            fiscalMonthStart,
            index
          );
          totalAllocatedRawAmounts.push(formattedValue);
          return {
            content: (
              <div className='text-neutral-dark text-right px-3'>
                {'$' + numberFormat(formattedValue)}
              </div>
            ),
            className: cellClass157px,
          };
        }),
        ...generate157pxEmptyCells(
          viewType === EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT ? 3 : 4
        ),
        generateActionCell(),
      ].filter(Boolean),
    },
  ];
}

export function generateVarianceRow(
  varianceList: number[],
  timeframe: TimeFrame,
  viewType: string
) {
  const totalRawAmount = varianceList.reduce((total, item) => {
    return total + item;
  }, 0);

  const totalAnnualCell = [];
  if (timeframe !== TimeFrame.YEARLY) {
    totalAnnualCell.push({
      content: (
        <div
          className={`${
            totalRawAmount < 0 ? 'text-error-darker' : 'text-neutral-dark'
          } text-right px-3`}
        >
          {totalRawAmount < 0 ? '-$' : '$'}
          {numberFormat(Math.abs(totalRawAmount))}
        </div>
      ),
      className: cellClass157px,
    });
  }

  return [
    {
      id: `123`,
      className: `group border ${mapRowColorWithLayer(2)}`,

      cells: [
        {
          content: (
            <div
              className='flex flex-row gap-x-1 min-w-200px cursor-pointer'
              style={{ paddingLeft: `${10}px` }}
            >
              <div className='text-neutral-dark'>Variance</div>
            </div>
          ),
          className: stickyCellsStyles(
            classnames('min-w-200px left-0 py-0 px-1'),
            2
          ),
        },
        ...totalAnnualCell,
        ...Array.from({
          length:
            timeframe === TimeFrame.QUARTERLY
              ? 4
              : timeframe === TimeFrame.MONTHLY
              ? 12
              : 1,
        }).map((_, index) => ({
          content: (
            <div
              className={`text-right px-3 ${
                varianceList[index] < 0
                  ? 'text-error-darker'
                  : 'text-neutral-dark'
              }`}
            >
              {varianceList[index] < 0 ? '-$' : '$'}
              {numberFormat(Math.abs(varianceList[index]))}
            </div>
          ),
          className: cellClass157px + ' text-right',
        })),
        ...generate157pxEmptyCells(
          viewType === EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT ? 3 : 4
        ),
        generateActionCell(),
      ].filter(Boolean),
    },
  ];
}

export function generateBudgetAmountRow(
  timeframe: TimeFrame,
  budgetAmounts: number[],
  onEditBudgetAmount: (budgetAmountReq: UpdateBudgetAmountRequest) => void,
  selectedBudget: BudgetModel,
  viewType: string
) {
  const totalBudgetAmounts = budgetAmounts.reduce((total, ba) => {
    return total + ba;
  }, 0);

  totalBudgetRawAmounts = [];
  const budgetAmountRowId = 'amountRow';

  const totalAnnualCell = [];
  if (timeframe !== TimeFrame.YEARLY) {
    totalAnnualCell.push({
      content: (
        <div className='text-neutral-dark text-right px-3'>
          {'$' + numberFormat(totalBudgetAmounts)}
        </div>
      ),
      className: cellClass157px + ' text-right',
    });
  }

  return [
    {
      id: budgetAmountRowId,
      className: `group border ${mapRowColorWithLayer(2)}`,
      cells: [
        {
          content: (
            <div
              className='flex flex-row gap-x-1 min-w-200px'
              style={{ paddingLeft: `${10}px` }}
            >
              <div className='text-neutral-dark'>Budget</div>
            </div>
          ),
          className: stickyCellsStyles(
            classnames('min-w-200px left-0 h-12 py-0 px-1'),
            2
          ),
        },
        ...totalAnnualCell,
        ...Array.from({
          length:
            timeframe === TimeFrame.QUARTERLY
              ? 4
              : timeframe === TimeFrame.MONTHLY
              ? 12
              : 1,
        }).map((_, index) => {
          const formattedAmount = budgetAmounts[index];
          totalBudgetRawAmounts.push(formattedAmount);
          return {
            content: (
              <div className='h-full py-1 flex items-center justify-end text-right '>
                <CurrencyField
                  className='h-full w-full mx-1'
                  inputClassName='h-full text-right'
                  value={parseFloat(
                    budgetAmounts[index]?.toFixed(2)
                  ).toString()}
                  onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    const { value, selectionStart } = e.currentTarget;
                    const key = e.key;

                    // Allow numbers, one decimal, and navigation keys (Backspace, Arrow keys)
                    if (
                      !/[0-9.]/.test(key) &&
                      !['Backspace', 'ArrowLeft', 'ArrowRight'].includes(key)
                    ) {
                      e.preventDefault();
                    }

                    // Prevent multiple decimal points
                    if (key === '.' && value.includes('.')) {
                      e.preventDefault();
                    }

                    // Ensure only up to two decimal places
                    if (
                      value.includes('.') &&
                      selectionStart !== null &&
                      value.indexOf('.') < selectionStart && // Only enforce if editing decimal
                      // part
                      value.split('.')[1]?.length >= 2
                    ) {
                      e.preventDefault();
                    }
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur(); // Triggers onBlur
                    }
                  }}
                  onBlur={(e: any) => {
                    const updatedValue = e.target.value;
                    onEditBudgetAmount({
                      budgetId: selectedBudget.id,
                      serial: index,
                      target: parseFloat(updatedValue),
                    });
                  }}
                />
              </div>
            ),
            className: cellClass157px + ' text-right',
          };
        }),
        ...generate157pxEmptyCells(
          viewType === EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT ? 3 : 4
        ),
        generateActionCell(),
      ].filter(Boolean),
    },
  ];
}

export function generateCostOfLaborRow(
  layer: number,
  onEditCostOfLabor: (
    projectId: string,
    lineItemId?: string,
    amounts?: number[]
  ) => void,
  viewType: string,
  fiscalMonthStart: number,
  timeframe: TimeFrame,
  projectId: string,
  handleAddNote: (item: LineItemWithAmounts) => void,
  handleAddAttachment: (itemId: string) => void,
  lineItemInfo?: LineItemWithAmounts
): Array<any> {
  if (!lineItemInfo) {
    lineItemInfo = {
      organizationId: '',
      budgetId: '',
      id: '',
      projectId: projectId,
      showInput: false,
      currency: '',
      amounts: [],
      rawAmounts: Array(12).fill(0),
      rawActuals: Array(12).fill(0),
    };
  }

  return [
    {
      id: projectId,
      className: `group border ${mapRowColorWithLayer(2)}`,
      cells: [
        {
          content: (
            <div
              className='flex flex-row min-w-200px px-3 cursor-pointer table-text-truncate items-center'
              style={{ paddingLeft: `${layer * 10 + 9}px` }}
            >
              Cost of Labor
            </div>
          ),
          className: stickyCellsStyles(classnames('min-w-200px left-0 p-0'), 2),
        },
        ...(timeframe !== TimeFrame.YEARLY
          ? [
              {
                content: (
                  <div className='overflow-hidden text-right px-3'>
                    {/*
                     * Cost of Labor is derived, not allocated: it comes from the
                     * projectsActualLaborCost endpoint via useProjectLaborOfCost,
                     * which only writes amounts[i].actual (never .allocation), so
                     * rawAmounts stays at 0 for any project without a persisted
                     * CoL line item. Read rawActuals here or the fiscal-year
                     * total renders $0 for every project even when the endpoint
                     * returned a real number (CGP-223 follow-up).
                     */}
                    {'$' +
                      numberFormat(
                        lineItemInfo.rawActuals.reduce(
                          (total: number, item: number) => total + (item ?? 0),
                          0
                        )
                      )}
                  </div>
                ),
                className: cellClass157px,
              },
            ]
          : []),
        ...Array.from({
          length:
            timeframe === TimeFrame.QUARTERLY
              ? 4
              : timeframe === TimeFrame.MONTHLY
              ? 12
              : 1,
        }).map((_, index) => ({
          content: (
            <div className={cellClass157pxInsideDiv + ' h-12 text-right'}>
              {/*
               * Cost of Labor is derived, not user-editable: useProjectLaborOfCost
               * rebuilds actuals from projectsActualLaborCost on every render, so
               * any locally-typed value would be clobbered on the next re-render.
               * The `amounts` field on this endpoint is also an *allocation* write
               * -- persisting a labor-cost value there would corrupt real per-month
               * allocations for projects with a persisted CoL line item, and
               * inflate the parent-project total (buildLineItemDataForPrograms
               * rolls rawAmounts into projectRow.rawAmounts). Keep the cell
               * read-only until CoL is refactored off the LineItem shape.
               */}
              <CurrencyField
                readOnly
                className='h-full w-full'
                inputClassName='h-full text-right'
                value={
                  timeframe === TimeFrame.MONTHLY
                    ? (
                        lineItemInfo!.rawActuals[
                          (fiscalMonthStart + index) % 12
                        ] ?? 0
                      )
                        .toFixed(2)
                        .toString()
                    : (lineItemInfo!.rawActuals[index] ?? 0)
                        .toFixed(2)
                        .toString()
                }
              />
            </div>
          ),
          className: cellClass157px,
        })),
        ...generate157pxEmptyCells(
          viewType === EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT ? 3 : 4
        ),
        {
          content: (
            <div className='min-w-120px px-3 grid grid-cols-3 gap-x-2'>
              <Button
                variant='tertiary'
                onClick={() => {
                  handleAddNote(lineItemInfo!);
                }}
                className='w-6 h-6 px-1'
              >
                <Icon name='chatbox-outline' />
              </Button>
              <Button
                variant='tertiary'
                onClick={() => {
                  handleAddAttachment(lineItemInfo!.id);
                }}
                className='w-6 h-6 px-1'
              >
                <Icon name='attach' />
              </Button>
            </div>
          ),
          className: 'h-12 min-w-120px py-0 px-1',
        },
      ].filter(Boolean),
    },
  ];
}

export const generateTableEmptyColumnForTimeframes = (
  timeFrame: TimeFrame
): any[] => {
  if (timeFrame === TimeFrame.QUARTERLY) {
    return Array.from({ length: 4 }).map(() => ({
      className: 'p-0',
    }));
  } else if (timeFrame === TimeFrame.MONTHLY) {
    return Array.from({ length: 12 }).map(() => ({
      className: 'p-0',
    }));
  } else if (timeFrame === TimeFrame.YEARLY) {
    return Array.from({ length: 1 }).map(() => ({
      className: 'p-0',
    }));
  } else {
    return [];
  }
};

function generate157pxEmptyCells(
  count: number
): { content: JSX.Element; className: string }[] {
  return Array.from({ length: count }, (_, index) => ({
    content: <div key={index} className='h-12'></div>,
    className: 'h-12 py-0 px-1',
  }));
}

function generateActionCell(): { content: JSX.Element; className: string } {
  return {
    content: (
      <div className='flex flex-row min-w-120px h-12'>
        {/*  Actions section */}
      </div>
    ),
    className: 'h-12 min-w-120px py-0 px-1',
  };
}

export const stickyCellsStyles = (className: string, layer: number) =>
  classnames(
    'border-transparent sticky z-1 ' + mapRowColorWithLayer(layer),
    className
  );
